/**
 * Creator Groups API Service
 * Handles all API calls for creator group management
 */

import { apiClient as api } from './apiClient';
import { API_BASE_URL } from '../config';

const GROUPS_API_BASE = `${API_BASE_URL}/api/v1/creator-groups`;

export interface CreatorGroup {
  id: string;
  creatorId: string;
  name: string;
  description?: string;
  memberCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMember {
  id: string;
  groupId: string;
  userId: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  respondedAt?: string;
  user?: {
    id: string;
    email: string;
    fullName?: string;
    username?: string;
  };
}

export interface JoinableGroup {
  id: string;
  name: string;
  description?: string;
  creatorId: string;
  membershipStatus: 'pending' | 'approved' | 'rejected' | null;
}

export const creatorGroupsService = {
  // ─── Group CRUD ─────────────────────────────────────────────────

  async createGroup(name: string, description?: string): Promise<CreatorGroup> {
    const response = await api.post<{ success: boolean; data: CreatorGroup }>(
      GROUPS_API_BASE,
      { name, description },
    );
    return response.data.data;
  },

  async listGroups(): Promise<CreatorGroup[]> {
    const response = await api.get<{ success: boolean; data: CreatorGroup[] }>(GROUPS_API_BASE);
    return response.data.data;
  },

  async updateGroup(groupId: string, data: { name?: string; description?: string }): Promise<CreatorGroup> {
    const response = await api.patch<{ success: boolean; data: CreatorGroup }>(
      `${GROUPS_API_BASE}/${groupId}`,
      data,
    );
    return response.data.data;
  },

  async deleteGroup(groupId: string): Promise<void> {
    await api.delete(`${GROUPS_API_BASE}/${groupId}`);
  },

  // ─── Member Management ──────────────────────────────────────────

  async addMembers(groupId: string, emails: string[]): Promise<{ added: string[]; notFound: string[]; duplicates: string[] }> {
    const response = await api.post<{ success: boolean; data: { added: string[]; notFound: string[]; duplicates: string[] } }>(
      `${GROUPS_API_BASE}/${groupId}/members`,
      { emails },
    );
    return response.data.data;
  },

  async listMembers(groupId: string, status?: string): Promise<GroupMember[]> {
    const response = await api.get<{ success: boolean; data: GroupMember[] }>(
      `${GROUPS_API_BASE}/${groupId}/members`,
      { params: status ? { status } : {} },
    );
    return response.data.data;
  },

  async updateMemberStatus(groupId: string, userId: string, status: 'approved' | 'rejected'): Promise<GroupMember> {
    const response = await api.patch<{ success: boolean; data: GroupMember }>(
      `${GROUPS_API_BASE}/${groupId}/members/${userId}`,
      { status },
    );
    return response.data.data;
  },

  async removeMember(groupId: string, userId: string): Promise<void> {
    await api.delete(`${GROUPS_API_BASE}/${groupId}/members/${userId}`);
  },

  // ─── Join Request ───────────────────────────────────────────────

  async requestJoin(groupId: string): Promise<GroupMember> {
    const response = await api.post<{ success: boolean; data: GroupMember }>(
      `${GROUPS_API_BASE}/${groupId}/join`,
    );
    return response.data.data;
  },

  async listJoinableGroups(creatorId: string): Promise<JoinableGroup[]> {
    const response = await api.get<{ success: boolean; data: JoinableGroup[] }>(
      `${GROUPS_API_BASE}/joinable`,
      { params: { creatorId } },
    );
    return response.data.data;
  },
};

export default creatorGroupsService;
